<template>
  <component
    :is="layoutComponent"
    page-title="Safety & Compliance"
    page-subtitle="Monitor safety metrics, report incidents, and maintain compliance standards"
  >
    <!-- Safety Content -->
    <div class="space-y-8 pt-8">
      <!-- Tab Navigation -->
      <div class="mb-8">
        <nav class="flex space-x-1 bg-slate-800/40 backdrop-blur-sm rounded-2xl p-2 border border-slate-700/50">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            class="flex-1 px-6 py-3 text-sm font-medium rounded-xl transition-all duration-200 group"
            :class="activeTab === tab.id 
              ? 'bg-gradient-to-r from-emerald-400 to-cyan-400 text-slate-900 shadow-lg shadow-emerald-500/25' 
              : 'text-slate-400 hover:text-emerald-400 hover:bg-slate-700/50'"
          >
            <div class="flex items-center justify-center space-x-2">
              <svg v-if="tab.id === 'safety-first'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
              <svg v-else-if="tab.id === 'certifications'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
              </svg>
              <svg v-else-if="tab.id === 'incidents'" class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
              <span>{{ tab.label }}</span>
            </div>
          </button>
        </nav>
      </div>

      <!-- Safety First Tab -->
      <div class="space-y-8" v-if="activeTab === 'safety-first'">
        <!-- Safety Overview Dashboard -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <div class="safety-stat-card">
            <div class="flex items-start justify-between">
              <div class="flex-1">
                <p class="text-sm font-medium text-slate-400 mb-2">Safety Score</p>
                <p class="text-3xl font-bold text-white">{{ safetyStats.safetyScore }}/100</p>
                <p class="text-sm text-emerald-400 mt-2">+{{ safetyStats.improvement }} this month</p>
              </div>
              <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon bg-gradient-to-br from-blue-500/20 to-indigo-500/20 border border-blue-500/30 rounded-2xl flex items-center justify-center text-blue-400">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-3a1 1 0 011-1h3a1 1 0 011 1v3a1 1 0 01-1 1h-3z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="stat-content">
              <p class="stat-label">Days Since Incident</p>
              <p class="stat-value">{{ safetyStats.daysSinceIncident }}</p>
              <p class="stat-change text-emerald-400">Record: {{ safetyStats.recordDays }}</p>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon bg-gradient-to-br from-emerald-500/20 to-cyan-500/20 border border-emerald-500/30 rounded-2xl flex items-center justify-center text-emerald-400">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="stat-content">
              <p class="stat-label">Active Crew</p>
              <p class="stat-value">{{ safetyStats.activeCrew }}</p>
              <p class="stat-change text-emerald-400">{{ safetyStats.compliantCrew }} compliant</p>
            </div>
          </div>

          <div class="stat-card">
            <div class="stat-icon bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/30 rounded-2xl flex items-center justify-center text-yellow-400">
              <svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clip-rule="evenodd"/>
              </svg>
            </div>
            <div class="stat-content">
              <p class="stat-label">Certifications</p>
              <p class="stat-value">{{ safetyStats.totalCertifications }}</p>
              <p class="stat-change text-yellow-400">{{ safetyStats.expiringSoon }} expiring</p>
            </div>
          </div>
        </div>

        <!-- Quick Actions -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div class="dashboard-action-card" @click="showIncidentModal = true">
            <div class="flex items-center space-x-4">
              <div class="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-white">Report Incident</h3>
                <p class="text-sm text-slate-400">Report safety incidents or near-misses</p>
              </div>
            </div>
          </div>

          <div class="dashboard-action-card" @click="showNearMissModal = true">
            <div class="flex items-center space-x-4">
              <div class="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-white">Near Miss</h3>
                <p class="text-sm text-slate-400">Log potential safety hazards</p>
              </div>
            </div>
          </div>

          <div class="dashboard-action-card" @click="showSafetyChecklistModal = true">
            <div class="flex items-center space-x-4">
              <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                </svg>
              </div>
              <div>
                <h3 class="text-lg font-semibold text-white">Safety Checklist</h3>
                <p class="text-sm text-slate-400">Complete daily safety checks</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Certifications Tab -->
      <div class="space-y-8" v-if="activeTab === 'certifications'">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <!-- My Certifications -->
          <div class="dashboard-card">
            <div class="flex items-center justify-between mb-6">
              <h3 class="text-xl font-bold text-white">My Certifications</h3>
              <button @click="showAddCertificationModal = true" class="btn-primary-modern">
                <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
                </svg>
                Add Certification
              </button>
            </div>
            
            <div class="space-y-4">
              <div v-for="cert in userCertifications" :key="cert.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 bg-emerald-500/20 rounded-lg flex items-center justify-center">
                    <svg class="w-5 h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-sm font-semibold text-white">{{ cert.name }}</h4>
                    <p class="text-xs text-slate-400">Expires: {{ formatDate(cert.expiryDate) }}</p>
                  </div>
                </div>
                <div class="flex items-center space-x-2">
                  <span :class="getCertificationStatusClass(cert.expiryDate)" class="px-2 py-1 text-xs rounded-full">
                    {{ getCertificationStatus(cert.expiryDate) }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- Available Certifications -->
          <div class="dashboard-card">
            <h3 class="text-xl font-bold text-white mb-6">Available Certifications</h3>
            <div class="space-y-4">
              <div v-for="cert in availableCertifications" :key="cert.id" class="flex items-center justify-between p-4 bg-slate-700/30 rounded-xl">
                <div class="flex items-center space-x-3">
                  <div class="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                    <svg class="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"/>
                    </svg>
                  </div>
                  <div>
                    <h4 class="text-sm font-semibold text-white">{{ cert.name }}</h4>
                    <p class="text-xs text-slate-400">{{ cert.description }}</p>
                  </div>
                </div>
                <button @click="enrollCertification(cert.id)" class="btn-primary-modern text-sm">
                  Enroll
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Incidents Tab -->
      <div class="space-y-8" v-if="activeTab === 'incidents'">
        <div class="dashboard-card">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-xl font-bold text-white">Incident History</h3>
            <button @click="showIncidentModal = true" class="btn-primary-modern">
              <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
              </svg>
              Report New Incident
            </button>
          </div>
          
          <div class="space-y-4">
            <div v-for="incident in incidents" :key="incident.id" class="p-4 bg-slate-700/30 rounded-xl">
              <div class="flex items-start justify-between">
                <div class="flex-1">
                  <div class="flex items-center space-x-2 mb-2">
                    <h4 class="text-sm font-semibold text-white">{{ incident.title }}</h4>
                    <span :class="getIncidentSeverityClass(incident.severity)" class="px-2 py-1 text-xs rounded-full">
                      {{ incident.severity }}
                    </span>
                  </div>
                  <p class="text-sm text-slate-400 mb-2">{{ incident.description }}</p>
                  <div class="flex items-center space-x-4 text-xs text-slate-500">
                    <span>Date: {{ formatDate(incident.date) }}</span>
                    <span>Location: {{ incident.location }}</span>
                    <span>Status: {{ incident.status }}</span>
                  </div>
                </div>
                <button class="text-red-400 hover:text-red-300 text-sm font-medium" @click="deleteIncident(incident)">
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import AppLayout from '../components/layout/AppLayout.vue'
import CompanyAppLayout from '../components/layout/CompanyAppLayout.vue'
import CompanyManAppLayout from '../components/layout/CompanyManAppLayout.vue'

const route = useRoute()
const userType = localStorage.getItem('user_type') || ''
const layoutComponent = computed(() => {
  if (userType === 'company_man' || route.path.startsWith('/company-man')) return CompanyManAppLayout
  if (userType === 'company' || route.path.startsWith('/company/')) return CompanyAppLayout
  return AppLayout
})

// Reactive data
const activeTab = ref('safety-first')
const showIncidentModal = ref(false)
const showNearMissModal = ref(false)
const showSafetyChecklistModal = ref(false)
const showAddCertificationModal = ref(false)

// Tabs configuration
const tabs = [
  { id: 'safety-first', label: 'Safety First' },
  { id: 'certifications', label: 'Certifications' },
  { id: 'incidents', label: 'Incidents' }
]

// Safety statistics
const safetyStats = reactive({
  safetyScore: 94,
  improvement: 3,
  daysSinceIncident: 127,
  recordDays: 365,
  activeCrew: 24,
  compliantCrew: 22,
  totalCertifications: 156,
  expiringSoon: 8
})

// User certifications
const userCertifications = ref([
  {
    id: 1,
    name: 'H2S Alive',
    expiryDate: '2024-12-15',
    status: 'active'
  },
  {
    id: 2,
    name: 'First Aid Level C',
    expiryDate: '2024-08-20',
    status: 'expiring'
  },
  {
    id: 3,
    name: 'Confined Space Entry',
    expiryDate: '2025-03-10',
    status: 'active'
  }
])

// Available certifications
const availableCertifications = ref([
  {
    id: 1,
    name: 'Fall Protection',
    description: 'Training for working at heights'
  },
  {
    id: 2,
    name: 'Fire Safety',
    description: 'Fire prevention and response'
  },
  {
    id: 3,
    name: 'Hazardous Materials',
    description: 'Handling dangerous substances'
  }
])

// Incidents
const incidents = ref([
  {
    id: 1,
    title: 'Minor Equipment Malfunction',
    description: 'Pump pressure gauge showing irregular readings',
    severity: 'Low',
    date: '2024-01-15',
    location: 'Well Site A',
    status: 'Resolved'
  },
  {
    id: 2,
    title: 'Near Miss - Vehicle Incident',
    description: 'Vehicle almost backed into equipment',
    severity: 'Medium',
    date: '2024-01-10',
    location: 'Well Site B',
    status: 'Under Investigation'
  }
])

// Methods
const deleteIncident = (incident: any) => {
  if (!window.confirm(`Delete incident “${incident.title}”? This cannot be undone.`)) return
  incidents.value = incidents.value.filter((item) => item.id !== incident.id)
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString()
}

const getCertificationStatus = (expiryDate: string) => {
  const daysUntilExpiry = Math.ceil((new Date(expiryDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))
  if (daysUntilExpiry < 0) return 'Expired'
  if (daysUntilExpiry <= 30) return 'Expiring Soon'
  return 'Active'
}

const getCertificationStatusClass = (expiryDate: string) => {
  const status = getCertificationStatus(expiryDate)
  if (status === 'Expired') return 'badge-danger'
  if (status === 'Expiring Soon') return 'badge-warning'
  return 'badge-success'
}

const getIncidentSeverityClass = (severity: string) => {
  switch (severity.toLowerCase()) {
    case 'high': return 'badge-danger'
    case 'medium': return 'badge-warning'
    case 'low': return 'badge-success'
    default: return 'badge-primary'
  }
}

const enrollCertification = (certId: number) => {
  console.log('Enrolling in certification:', certId)
  // Add enrollment logic here
}

// Lifecycle
onMounted(() => {
  console.log('Safety page mounted')
})
</script>

<style scoped>
.safety-stat-card {
  @apply bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50;
}

.stat-card {
  @apply bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50;
}

.stat-icon {
  @apply w-12 h-12 rounded-xl flex items-center justify-center;
}

.stat-content {
  @apply flex-1 ml-4;
}

.stat-label {
  @apply text-sm font-medium text-slate-400 mb-1;
}

.stat-value {
  @apply text-2xl font-bold text-white mb-1;
}

.stat-change {
  @apply text-sm;
}

.dashboard-action-card {
  @apply bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300 cursor-pointer;
}

.dashboard-card {
  @apply bg-slate-800/90 backdrop-blur-xl rounded-3xl p-6 border border-slate-700/50;
}

.btn-primary-modern {
  @apply px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-xl hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}

.badge-success {
  @apply bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 px-2 py-1 text-xs rounded-full;
}

.badge-warning {
  @apply bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 px-2 py-1 text-xs rounded-full;
}

.badge-danger {
  @apply bg-red-500/20 text-red-400 border border-red-500/30 px-2 py-1 text-xs rounded-full;
}

.badge-primary {
  @apply bg-blue-500/20 text-blue-400 border border-blue-500/30 px-2 py-1 text-xs rounded-full;
}
</style>
